import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { animateScroll } from 'react-scroll';
import TextField from 'components/TextField';
import NavigationButtons from 'components/NavigationButtons';
import i18n from 'utils/translate';
import { numbersOnly, noNumbers, startWithNumber } from 'utils/validators';
import Accordion from 'components/Accordion';
import AccordionItem from 'components/AccordionItem';
import { formatPrice } from 'utils/common';
import { toast } from 'react-toastify';
import './styles.scss';
import { GENDER_FEMALE, GENDER_MALE } from 'utils/constants';
import ErrorMessage from 'components/ErrorMessage';
import { linkTypeShape } from 'utils/types';
import FilterField from 'components-shared/FilterField';
import { updateRegionsState, filterZipsOrCommunitiesBySelection } from 'utils/regions';
import { isPersonalDataFormValid } from 'utils/validators';

const propTypes = {
  ebike: PropTypes.bool.isRequired,
  bikePrice: PropTypes.number.isRequired,
  start_date: PropTypes.string,
  end_date: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  backLink: PropTypes.shape(linkTypeShape),
  nextLink: PropTypes.shape(linkTypeShape).isRequired,
  renderSummaryBox: PropTypes.element,
  quote: PropTypes.shape({
    price: PropTypes.number.isRequired,
    currency: PropTypes.string.isRequired
  })
};

const defaultProps = {
  onChange: console.log
};

class PersonalData extends Component {
  constructor(props) {
    super(props);

    this.state = {
      anrede: 0,
      geburtsdatum: props.birthdate,
      submitted: false,
      validForm: false,
      dataLoaded: false,
      regions: [],
      communities: [],
      allCommunities: [],
      zips: [],
      allZips: [],
      land: 'Österreich',
      ...props.data
    };
  }

  loadRegions = async () => {
    const callback = () => {
      const currentSelectedZip = this.props.data?.plz;

      if (currentSelectedZip) {
        this.setState({ locationFirstChangedBy: 'plz' });
        filterZipsOrCommunitiesBySelection(this, currentSelectedZip, 'plz');
      }
    };
    await updateRegionsState(this, callback);
  };

  handleZipOrCommunityChange = (newValue, key) => {
    const { locationFirstChangedBy, allCommunities, allZips } = this.state;
    const opposingStateKey = key === 'plz' ? 'ort' : 'plz';
    const opposingStateKeyOptions = key === 'plz' ? 'communities' : 'zips';

    // the opposing options (e.g. communities) can only be controlled via the locationFirstChangedBy option (zip)
    // or the other way around
    if (!locationFirstChangedBy) {
      this.setState({ locationFirstChangedBy: key });
    }

    this.handleDataChange(newValue, key);

    // we reset all values to '' and list all options
    if (!newValue && locationFirstChangedBy !== opposingStateKey) {
      const options = key === 'plz' ? allCommunities : allZips;
      this.setState({ locationFirstChangedBy: '', [opposingStateKeyOptions]: options });
      this.handleDataChange(undefined, opposingStateKey);
      return;
    }

    // the filtered options are already in place
    if (locationFirstChangedBy === opposingStateKey) return;

    filterZipsOrCommunitiesBySelection(this, newValue, key);
  };

  handleSubmit = () => {
    const {
      nextLink: { go } = {
        go: () => {}
      },
      onChange
    } = this.props;
    const form = this.refs.form;

    if (form.checkValidity && !form.checkValidity()) {
      form.reportValidity();
      return;
    }

    onChange(this.state);
    go();

    window.dataLayer.push({
      event: 'onlineabschluss',
      formname: 'Fahrraddiebstahl-Versicherung',
      action: 'Schritt 2 von 4'
    });
  };

  handleDataChange = (data, key) => {
    const { submitted } = this.state;
    const { onChange } = this.props;

    this.setState(
      {
        [`${key}`]: data
      },
      () => {
        onChange(this.state);
        if (submitted) {
          this.setState({
            validForm: this.refs.form.checkValidity()
          });
        }
      }
    );
  };

  componentDidMount() {
    animateScroll.scrollToTop({ duration: 0 });
    this.loadRegions();
  }

  render() {
    const {
      anrede,
      vorname,
      nachname,
      strasse,
      nr,
      plz,
      ort,
      email,
      telefon,
      submitted,
      geburtsdatum,
      zips,
      communities,
      dataLoaded,
      land
    } = this.state;
    const {
      backLink,
      quote: { price, currency },
      renderSummaryBox
    } = this.props;

    const titleOptions = [
      {
        value: GENDER_MALE,
        label: i18n('Herr')
      },
      {
        value: GENDER_FEMALE,
        label: i18n('Frau')
      }
    ];

    return (
      <div className="row">
        <div className="col-xs-12 col-md-8">
          <form
            className={`page-step-wrapper page-step-wrapper--narrow ${submitted ? 'submitted' : ''}`}
            ref="form"
            onSubmit={this.handleSubmit}
            id="insurance-form"
          >
            <Accordion>
              <AccordionItem header="Persönliche Angaben" id="person" className="highlighted" defaultOpen={true}>
                <div className="row">
                  <FilterField
                    label={i18n('Anrede')}
                    name="title"
                    className="col-xs-12 col-md-6 col-lg-3"
                    options={titleOptions}
                    ref="title"
                    isSearchable={false}
                    required
                    value={anrede}
                    onChange={data => this.handleDataChange(data?.value, 'anrede')}
                  />
                  <TextField
                    label={i18n('Vorname')}
                    name="first_name"
                    className="col-xs-12 col-md-6 col-lg-3"
                    required
                    value={vorname}
                    onChange={data => this.handleDataChange(data, 'vorname')}
                    ref="name"
                    pattern={noNumbers}
                    maxLength={13}
                  />
                  <TextField
                    label={i18n('Nachname')}
                    name="last_name"
                    className="col-xs-12 col-md-6 col-lg-3"
                    required
                    value={nachname}
                    onChange={data => this.handleDataChange(data, 'nachname')}
                    ref="lastname"
                    pattern={noNumbers}
                    maxLength={31}
                  />
                  <TextField
                    label={i18n('Geburtsdatum')}
                    name="birthdate"
                    className="col-xs-12 col-md-6 col-lg-3"
                    required
                    value={moment(geburtsdatum).format('DD.MM.YYYY')}
                    disabled
                  />
                </div>
              </AccordionItem>
              <AccordionItem header={i18n('Wohnadresse')} id="address" className="highlighted" defaultOpen={true}>
                <div className="row">
                  <TextField
                    label={i18n('Straße')}
                    name="street"
                    className="col-xs-12 col-lg-8 col-xl-4"
                    required
                    value={strasse}
                    onChange={data => this.handleDataChange(data, 'strasse')}
                    ref="street"
                    maxLength={27}
                  />
                  <TextField
                    label={i18n('Nr.')}
                    name="house_number"
                    className="col-xs-6 col-lg-4 col-xl-2"
                    required
                    value={nr}
                    onChange={data => this.handleDataChange(data, 'nr')}
                    ref="number"
                    maxLength={10}
                    pattern={startWithNumber}
                  />
                  <FilterField
                    label={i18n('PLZ')}
                    name="postal_code"
                    className="col-xs-12 col-lg-4 col-xl-2"
                    value={plz}
                    options={zips}
                    onChange={data => this.handleZipOrCommunityChange(data?.value, 'plz')}
                    isDisabled={!dataLoaded}
                    required
                    ref="plz"
                    isFormSubmitted={submitted}
                  />
                  <FilterField
                    label={i18n('Ort')}
                    name="community"
                    className="col-xs-12 col-lg-4 col-xl-2"
                    value={ort}
                    options={communities}
                    onChange={data => this.handleZipOrCommunityChange(data?.value, 'ort')}
                    required
                    ref="community"
                    isDisabled={!dataLoaded}
                    isFormSubmitted={submitted}
                  />
                  <TextField
                    label={i18n('Land')}
                    name="country"
                    className="col-xs-12 col-lg-4 col-xl-2"
                    value={land}
                    disabled
                    questionMark={i18n('Wohnort muss in Österreich liegen!')}
                    ref="country"
                  />
                </div>
              </AccordionItem>
              <AccordionItem header={i18n('Kontakt')} id="contact" className="highlighted" defaultOpen={true}>
                <div className="row">
                  <TextField
                    label={i18n('E-Mail')}
                    name="email"
                    type="email"
                    className="col-xs-12 col-lg-6 col-xl-3"
                    required
                    value={email}
                    onChange={data => this.handleDataChange(data, 'email')}
                    ref="mail"
                    maxLength={70}
                  />
                  <TextField
                    label={i18n('Telefon')}
                    name="phone"
                    type="tel"
                    className="col-xs-12 col-lg-6 col-xl-3"
                    required
                    pattern={numbersOnly}
                    value={telefon}
                    onChange={data => this.handleDataChange(data, 'telefon')}
                    ref="phone"
                    maxLength={20}
                  />
                </div>
              </AccordionItem>
            </Accordion>
            <div className="sticky-footer">
              <div className="page-step-wrapper__price d-md-none">
                {i18n('Prämie (jährlich)')}&nbsp;
                {formatPrice(price, currency)}
              </div>
              <NavigationButtons
                backLink={backLink}
                nextLink={{
                  go: () => {
                    this.setState({
                      submitted: true,
                      validForm: isPersonalDataFormValid(this)
                    });

                    Object.keys(this.refs).forEach(key => {
                      if (this.refs[key].onBlur) {
                        this.refs[key].onBlur();
                      }
                    });

                    if (isPersonalDataFormValid(this)) {
                      toast.dismiss();
                      this.handleSubmit();
                    } else {
                      toast.error(<ErrorMessage message={i18n('Bitte überprüfen Sie Ihre Angaben')} />);
                    }
                  }
                }}
              />
            </div>
          </form>
        </div>
        <div className="summary-wrapper col-md-4">{renderSummaryBox}</div>
      </div>
    );
  }
}

PersonalData.defaultProps = defaultProps;
PersonalData.propTypes = propTypes;

export default PersonalData;
